import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

const STORAGE_KEY = "clubs_view"

export default class extends Controller {
  static targets = ["results", "pagination", "form", "count", "viewList", "viewCards"]
  static values = { apiUrl: String }

  connect() {
    loadTranslations()
    this.filterTimeout = null
    this.loading = false
    this.view = (typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "cards"
    this._applyView()
    this._fetch(1)
  }

  disconnect() {
    clearTimeout(this.filterTimeout)
  }

  onFilterChange() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => this._fetch(1), 200)
  }

  onSearchInput() {
    const val = (this.formTarget?.querySelector('[name="search"]')?.value || "").trim()
    if (val.length >= 2 || val.length === 0) {
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => this._fetch(1), 350)
    }
  }

  onSubmit(event) {
    event.preventDefault()
    this._fetch(1)
  }

  setViewList() {
    this.view = "list"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "list")
    if (this._lastData) this._renderClubs(this._lastData, this._lastMeta)
  }

  setViewCards() {
    this.view = "cards"
    this._applyView()
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "cards")
    if (this._lastData) this._renderClubs(this._lastData, this._lastMeta)
  }

  _applyView() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.classList.remove("clubs-view-list", "clubs-view-cards")
    this.resultsTarget.classList.add(`clubs-view-${this.view}`)
    if (this.hasViewListTarget) this.viewListTarget.classList.toggle("active", this.view === "list")
    if (this.hasViewCardsTarget) this.viewCardsTarget.classList.toggle("active", this.view === "cards")
  }

  async _fetch(page) {
    if (this.loading) return
    this.loading = true

    if (this.hasResultsTarget) {
      this.resultsTarget.style.opacity = "0.5"
      this.resultsTarget.style.pointerEvents = "none"
    }

    try {
      const params = this._buildParams(page)
      const json = await apiGet(this.apiUrlValue, params)
      if (!json) return

      const { data, meta } = json
      this._lastData = data
      this._lastMeta = meta

      this._renderClubs(data, meta)

      if (this.hasPaginationTarget) {
        this.paginationTarget.innerHTML = meta.total_pages > 1 ? this._renderPagination(meta, params) : ""
      }

      if (this.hasCountTarget) this.countTarget.textContent = String(meta.total_count)
    } catch (error) {
      console.error("Error fetching clubs:", error)
      if (this.hasResultsTarget) {
        this.resultsTarget.innerHTML = `<div class="col-12 text-center py-5 text-danger">${escapeHtml(t("common.error") || "Błąd")}</div>`
      }
    } finally {
      this.loading = false
      if (this.hasResultsTarget) {
        this.resultsTarget.style.opacity = ""
        this.resultsTarget.style.pointerEvents = ""
      }
    }
  }

  _buildParams(page) {
    const params = { page }
    if (!this.hasFormTarget) return params

    const formData = new FormData(this.formTarget)
    const search = (formData.get("search") || "").trim()
    if (search.length >= 2) params.search = search

    const city = (formData.get("city") || "").trim()
    if (city) params.city = city

    const voivodeship = formData.get("voivodeship")
    if (voivodeship) params.voivodeship = voivodeship

    return params
  }

  _renderClubs(clubs, meta) {
    if (!clubs.length) {
      this.resultsTarget.innerHTML = `
        <div class="text-center py-5">
          <i class="fa-solid fa-building fa-3x text-muted mb-3"></i>
          <h4>${escapeHtml(t("index.no_clubs"))}</h4>
          <p class="text-muted">${escapeHtml(t("index.no_clubs_hint"))}</p>
          <a href="/clubs/search" class="btn btn-outline-primary mt-3">${escapeHtml(t("index.search_clubs", { default: "Szukaj klubów" }))}</a>
        </div>`
      return
    }

    if (this.view === "list") {
      this.resultsTarget.innerHTML = this._renderListView(clubs)
    } else {
      this.resultsTarget.innerHTML = `<div class="row g-4">${clubs.map((c) => this._clubCard(c)).join("")}</div>`
    }
  }

  _renderListView(clubs) {
    const rows = clubs.map((c) => this._clubRow(c))
    return `
      <div class="table-responsive">
        <table class="table table-sm table-hover align-middle">
          <tbody>${rows.join("")}</tbody>
        </table>
      </div>`
  }

  _clubRow(c) {
    const name = escapeHtml(c.name)
    const city = c.city ? escapeHtml(c.city) : ""
    const voivodeship = c.voivodeship ? escapeHtml(c.voivodeship) : ""
    const location = [city, voivodeship].filter(Boolean).join(", ") || "-"
    const trialBadge = c.on_trial
      ? `<span class="badge bg-info small">${escapeHtml(t("index.on_trial"))}</span>`
      : ""

    return `
      <tr>
        <td>
          <a href="/clubs/${escapeHtml(c.slug)}" class="text-decoration-none fw-medium text-dark">${name}</a>
        </td>
        <td class="text-muted small">${location}</td>
        <td>${trialBadge}</td>
      </tr>`
  }

  _clubCard(c) {
    const name = escapeHtml(c.name)
    const cityHtml = c.city
      ? `<p class="card-text text-muted small mb-0"><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(c.city)}</p>`
      : ""
    const trialBadge = c.on_trial
      ? `<span class="badge bg-info mt-2">${escapeHtml(t("index.on_trial"))}</span>`
      : ""

    return `
      <div class="col-lg-3 col-md-4 col-sm-6 col-12">
        <a href="/clubs/${escapeHtml(c.slug)}" class="card shadow-sm h-100 text-decoration-none text-dark d-block">
          <div class="card-body">
            <h5 class="card-title">${name}</h5>
            ${cityHtml}${trialBadge}
          </div>
        </a>
      </div>`
  }

  _renderPagination(meta, params) {
    const { current_page, total_pages } = meta
    const prev = current_page > 1 ? current_page - 1 : null
    const next = current_page < total_pages ? current_page + 1 : null

    let html = '<ul class="default-pagination lab-ul justify-content-center">'

    html += prev
      ? `<li><a href="#" data-action="click->clubs-index#goToPage" data-page="${prev}"><i class="fa-solid fa-angle-left"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-left"></i></span></li>'

    const start = Math.max(1, current_page - 2)
    const end = Math.min(total_pages, current_page + 2)

    for (let i = start; i <= end; i++) {
      const active = i === current_page ? ' class="active"' : ""
      html += `<li><a href="#" data-action="click->clubs-index#goToPage" data-page="${i}"${active}>${String(i).padStart(2, "0")}</a></li>`
    }

    html += next
      ? `<li><a href="#" data-action="click->clubs-index#goToPage" data-page="${next}"><i class="fa-solid fa-angle-right"></i></a></li>`
      : '<li><span class="text-muted"><i class="fa-solid fa-angle-right"></i></span></li>'

    html += "</ul>"
    return html
  }

  goToPage(event) {
    event.preventDefault()
    const page = event.currentTarget.dataset.page
    if (page) this._fetch(parseInt(page, 10))
  }
}
